import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Home,
  Check,
  AlertCircle,
  Loader2,
  LogIn,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, useAuthStore } from '@/stores/auth/auth-store';
import {
  HandoffAcceptanceResponse,
  HandoffTokenValidationResponse,
  serviceProviderApi,
} from '@/services/api/serviceProviderApi';
import {
  FOREST_GREEN,
  ERROR,
  WARNING,
  TEXT,
  BACKGROUND,
  MID_GRAY,
  SHADOWS,
} from '@/styles/tokens';
import { TerraMatrixBrand } from '@/components/branding/TerraMatrixBrand';
import * as errorUtils from '@/utils/errors';

type PageState =
  | 'loading'
  | 'valid'
  | 'invalid'
  | 'already_accepted'
  | 'login_required'
  | 'registering'
  | 'processing'
  | 'success'
  | 'declined'
  | 'error';

export const CustomerOnboardingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [propertyInfo, setPropertyInfo] = useState<HandoffTokenValidationResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  // Registration form state
  const [regForm, setRegForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [regErrors, setRegErrors] = useState<Record<string, string>>({});

  // Validate token on mount
  const validateToken = useCallback(async () => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    try {
      const response = await serviceProviderApi.validateHandoffToken(token);
      setPropertyInfo(response.data ?? null);
      setPageState(isAuthenticated ? 'valid' : 'login_required');
    } catch (error: unknown) {
      const status = errorUtils.getErrorStatus(error);
      if (status === 410) {
        setPageState('already_accepted');
      } else if (status === 404) {
        setPageState('invalid');
      } else {
        setPageState('error');
        setErrorMessage(errorUtils.getApiErrorMessage(error, 'Failed to validate invitation'));
      }
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Handle accept (authenticated user)
  const handleAccept = async () => {
    if (!token) return;
    setPageState('processing');

    try {
      const response = await serviceProviderApi.acceptHandoff(token);
      const result = response.data;
      if (!result) {
        throw new Error('Missing handoff response payload');
      }

      // Store tokens and user in auth store
      if (result.tokens) {
        useAuthStore.getState().setTokens(result.tokens);
      }
      if (result.user) {
        useAuthStore.getState().setUser(result.user);
      }

      setPageState('success');
      toast.success('Welcome! Your system is ready.');

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: unknown) {
      setPageState('error');
      setErrorMessage(errorUtils.getApiErrorMessage(error, 'Failed to accept invitation'));
      toast.error('Failed to accept invitation');
    }
  };

  // Handle register + accept
  const handleRegisterAndAccept = async () => {
    if (!token) return;

    // Validate
    const errors: Record<string, string> = {};
    if (!regForm.email) errors.email = 'Email is required';
    if (!regForm.firstName) errors.firstName = 'First name is required';
    if (!regForm.lastName) errors.lastName = 'Last name is required';
    if (!regForm.password || regForm.password.length < 8) errors.password = 'Password must be at least 8 characters'; // pragma: allowlist secret
    if (regForm.password !== regForm.confirmPassword) errors.confirmPassword = 'Passwords do not match'; // pragma: allowlist secret
    setRegErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setPageState('processing');

    try {
      const response = await serviceProviderApi.acceptHandoffWithRegistration(token, {
        email: regForm.email,
        password: regForm.password,
        firstName: regForm.firstName,
        lastName: regForm.lastName,
      });
      const result = response.data;
      if (!result) {
        throw new Error('Missing registration response payload');
      }

      if (result.tokens) {
        useAuthStore.getState().setTokens(result.tokens);
      }
      if (result.user) {
        useAuthStore.getState().setUser(result.user);
      }

      setPageState('success');
      toast.success('Account created! Your system is ready.');

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: unknown) {
      const status = errorUtils.getErrorStatus(error);
      if (status === 409) {
        // Email already exists — switch to login flow
        setPageState('login_required');
        toast.info('An account with this email already exists. Please sign in to accept.');
      } else {
        setPageState('error');
        setErrorMessage(errorUtils.getApiErrorMessage(error, 'Failed to register'));
        toast.error('Failed to create account');
      }
    }
  };

  // Handle decline
  const handleDecline = async () => {
    if (!token) return;
    setShowDeclineConfirm(false);
    setPageState('processing');

    try {
      await serviceProviderApi.declineHandoff(token);
      setPageState('declined');
      toast.success('Invitation declined');
    } catch (error: unknown) {
      setPageState('error');
      setErrorMessage(errorUtils.getApiErrorMessage(error, 'Failed to decline'));
    }
  };

  // Handle login redirect
  const handleLogin = () => {
    sessionStorage.setItem('onboarding_redirect', window.location.pathname);
    navigate('/login');
  };

  // Format address
  const formatAddress = (address?: HandoffTokenValidationResponse['address']) => {
    if (!address) return null;
    const parts = [address.street, address.city, address.state, address.postalCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: BACKGROUND.secondary }}
    >
      {/* Header with logo */}
      <div className="mb-8">
        <Link to="/">
          <TerraMatrixBrand
            markClassName="h-10 w-10"
            nameClassName="text-3xl"
          />
        </Link>
      </div>

      {/* Card container */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          backgroundColor: BACKGROUND.primary,
          boxShadow: SHADOWS.xl,
        }}
      >
        {/* Loading State */}
        {pageState === 'loading' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Validating Invitation</h2>
            <p className="text-muted-foreground">
              Please wait while we verify your invitation...
            </p>
          </div>
        )}

        {/* Valid State — authenticated user, ready to accept */}
        {pageState === 'valid' && propertyInfo && (
          <>
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <Home className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">Your System is Ready</h2>
              <h1 className="text-2xl font-bold mt-1">{propertyInfo.propertyName}</h1>
            </div>

            <div className="p-6">
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Set up by</span>
                  <span className="font-medium">{propertyInfo.serviceProviderName}</span>
                </div>
                {propertyInfo.serviceProviderBusinessName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Business</span>
                    <span className="font-medium">{propertyInfo.serviceProviderBusinessName}</span>
                  </div>
                )}
                {formatAddress(propertyInfo.address) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Address</span>
                    <span className="font-medium text-right max-w-[200px]">
                      {formatAddress(propertyInfo.address)}
                    </span>
                  </div>
                )}
              </div>

              <Button
                className="w-full mb-3"
                onClick={handleAccept}
                style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
              >
                <Check className="h-4 w-4 mr-2" />
                Accept & Get Started
              </Button>

              {!showDeclineConfirm ? (
                <button
                  className="w-full text-center text-sm py-2"
                  style={{ color: MID_GRAY.DEFAULT }}
                  onClick={() => setShowDeclineConfirm(true)}
                >
                  Decline this invitation
                </button>
              ) : (
                <div className="p-3 rounded-lg" style={{ backgroundColor: BACKGROUND.secondary }}>
                  <p className="text-sm mb-3" style={{ color: TEXT.secondary }}>
                    Are you sure? The service provider will be notified.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setShowDeclineConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      onClick={handleDecline}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Login Required State */}
        {pageState === 'login_required' && propertyInfo && (
          <>
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <Home className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">Your System is Ready</h2>
              <h1 className="text-2xl font-bold mt-1">{propertyInfo.propertyName}</h1>
            </div>

            <div className="p-6">
              <div className="space-y-3 mb-6 p-4 rounded-lg" style={{ backgroundColor: BACKGROUND.secondary }}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Set up by</span>
                  <span className="font-medium">{propertyInfo.serviceProviderName}</span>
                </div>
                {propertyInfo.serviceProviderBusinessName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Business</span>
                    <span className="font-medium">{propertyInfo.serviceProviderBusinessName}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-center mb-4" style={{ color: TEXT.secondary }}>
                Sign in to your account or create one to accept this system.
              </p>

              <Button
                className="w-full mb-3"
                onClick={handleLogin}
                style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In to Accept
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setPageState('registering')}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create Account
              </Button>

              <button
                className="w-full text-center text-sm py-2 mt-2"
                style={{ color: MID_GRAY.DEFAULT }}
                onClick={() => setShowDeclineConfirm(true)}
              >
                Decline this invitation
              </button>

              {showDeclineConfirm && (
                <div className="p-3 rounded-lg mt-2" style={{ backgroundColor: BACKGROUND.secondary }}>
                  <p className="text-sm mb-3" style={{ color: TEXT.secondary }}>
                    Are you sure? The service provider will be notified.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setShowDeclineConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      onClick={handleDecline}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Registering State — inline registration form */}
        {pageState === 'registering' && propertyInfo && (
          <>
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">Create Your Account</h2>
              <p className="text-sm opacity-80 mt-1">for {propertyInfo.propertyName}</p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                  {regErrors.email && <p className="text-sm mt-1" style={{ color: ERROR.DEFAULT }}>{regErrors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="reg-first">First Name</Label>
                    <Input
                      id="reg-first"
                      value={regForm.firstName}
                      onChange={(e) => setRegForm({ ...regForm, firstName: e.target.value })}
                    />
                    {regErrors.firstName && <p className="text-sm mt-1" style={{ color: ERROR.DEFAULT }}>{regErrors.firstName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="reg-last">Last Name</Label>
                    <Input
                      id="reg-last"
                      value={regForm.lastName}
                      onChange={(e) => setRegForm({ ...regForm, lastName: e.target.value })}
                    />
                    {regErrors.lastName && <p className="text-sm mt-1" style={{ color: ERROR.DEFAULT }}>{regErrors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="reg-password">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    placeholder="At least 8 characters"
                  />
                  {regErrors.password && <p className="text-sm mt-1" style={{ color: ERROR.DEFAULT }}>{regErrors.password}</p>}
                </div>

                <div>
                  <Label htmlFor="reg-confirm">Confirm Password</Label>
                  <Input
                    id="reg-confirm"
                    type="password"
                    value={regForm.confirmPassword}
                    onChange={(e) => setRegForm({ ...regForm, confirmPassword: e.target.value })}
                  />
                  {regErrors.confirmPassword && <p className="text-sm mt-1" style={{ color: ERROR.DEFAULT }}>{regErrors.confirmPassword}</p>}
                </div>
              </div>

              <Button
                className="w-full mt-6"
                onClick={handleRegisterAndAccept}
                style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
              >
                <Check className="h-4 w-4 mr-2" />
                Create Account & Accept
              </Button>

              <button
                className="w-full text-center text-sm py-2 mt-2"
                style={{ color: MID_GRAY.DEFAULT }}
                onClick={() => setPageState('login_required')}
              >
                Already have an account? Sign in
              </button>
            </div>
          </>
        )}

        {/* Processing State */}
        {pageState === 'processing' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Processing</h2>
            <p className="text-muted-foreground">Setting up your system...</p>
          </div>
        )}

        {/* Success State */}
        {pageState === 'success' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <Check className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Welcome!</h2>
            <p className="text-muted-foreground mb-4">
              Your system is ready. Redirecting to your dashboard...
            </p>
            <Loader2
              className="h-5 w-5 animate-spin mx-auto"
              style={{ color: MID_GRAY.DEFAULT }}
            />
          </div>
        )}

        {/* Declined State */}
        {pageState === 'declined' && (
          <div className="p-8 text-center">
            <XCircle
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: MID_GRAY.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Invitation Declined</h2>
            <p className="text-muted-foreground mb-6">
              You have declined this invitation. The service provider has been notified.
            </p>
            <Link to="/">
              <Button variant="outline">Return Home</Button>
            </Link>
          </div>
        )}

        {/* Invalid State */}
        {pageState === 'invalid' && (
          <div className="p-8 text-center">
            <AlertCircle
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: WARNING.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">
              This invitation link is invalid or has expired. Please contact your service provider for a new link.
            </p>
            <Link to="/">
              <Button variant="outline">Return Home</Button>
            </Link>
          </div>
        )}

        {/* Already Accepted State */}
        {pageState === 'already_accepted' && (
          <div className="p-8 text-center">
            <Check
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Already Accepted</h2>
            <p className="text-muted-foreground mb-6">
              This invitation has already been accepted. Sign in to access your system.
            </p>
            <Link to="/login">
              <Button style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}>
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
            </Link>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="p-8 text-center">
            <AlertCircle
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: ERROR.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || 'An unexpected error occurred. Please try again.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => validateToken()}>
                Try Again
              </Button>
              <Link to="/">
                <Button variant="outline">Return Home</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
